(() => {
  function enableMouseDrag(rail) {
    const abort = new AbortController();
    const on = (node, name, handler, options = {}) => node.addEventListener(name, handler, { ...options, signal: abort.signal });
    let press = null, suppressClick = false;
    const clearClick = () => { suppressClick = false; };
    const finish = () => {
      if (!press) return;
      const { id, dragging } = press;
      press = null;
      rail.classList.remove('is-mouse-dragging');
      if (rail.hasPointerCapture(id)) rail.releasePointerCapture(id);
      if (dragging) suppressClick = true;
    };
    rail.classList.add('has-mouse-drag');
    on(rail, 'pointerdown', (event) => {
      clearClick();
      const control = event.target.closest('button, input, select, textarea, [contenteditable="true"]');
      if (event.pointerType !== 'mouse' || event.button !== 0 || !event.isPrimary ||
          event.ctrlKey || event.metaKey || event.altKey || event.shiftKey ||
          rail.scrollWidth <= rail.clientWidth + 1 ||
          (control && !(rail.dataset.mouseDrag === 'tabs' && control.matches('button[role="tab"]')))) return;
      finish();
      clearClick();
      press = { id: event.pointerId, x: event.clientX, y: event.clientY, left: rail.scrollLeft, dragging: false };
    });
    on(window, 'pointermove', (event) => {
      if (!press || event.pointerId !== press.id) return;
      if (!(event.buttons & 1)) { finish(); return; }
      const dx = event.clientX - press.x;
      const dy = event.clientY - press.y;
      if (!press.dragging) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 6) return;
        if (Math.abs(dy) >= Math.abs(dx)) { finish(); return; }
        press.dragging = true;
        rail.classList.add('is-mouse-dragging');
        rail.setPointerCapture(press.id);
      }
      event.preventDefault();
      rail.scrollLeft = press.left - dx;
    }, { passive: false });
    on(window, 'pointerup', (event) => { if (event.pointerId === press?.id) finish(); });
    on(window, 'pointercancel', (event) => { if (event.pointerId === press?.id) finish(); });
    on(rail, 'lostpointercapture', finish);
    on(window, 'blur', finish);
    on(window, 'keydown', (event) => { if (event.key === 'Escape') finish(); });
    on(rail, 'dragstart', (event) => event.preventDefault());
    on(rail, 'click', (event) => {
      if (!suppressClick || event.detail === 0) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      clearClick();
    }, { capture: true });
    return () => {
      abort.abort();
      finish();
      clearClick();
      rail.classList.remove('has-mouse-drag');
    };
  }

  if (window.rosariumDubaiHome) return;
  const instances = new WeakMap();
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const selector = '[data-rdb-home]';

  class HomeSection {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.cleanups = [];
      this.on = (node, name, fn, options = {}) => node?.addEventListener(name, fn, { ...options, signal: this.abort.signal });
      this.mountHeroDock();
      this.mountHeroMessages();
      this.mountRails();
      this.mountTabs();
      this.mountBudgetEntrance();
      this.mountReels();
      this.mountClientStrip();
      this.on(document, 'shopify:block:select', (event) => {
        if (!this.root.contains(event.target)) return;
        const item = event.target.closest('.rail > li');
        item?.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
      });
    }

    observe(Observer, callback, options) {
      const observer = new Observer(callback, options);
      this.cleanups.push(() => observer.disconnect());
      return observer;
    }

    mountHeroDock() {
      const hero = this.root.querySelector('.hero');
      const copy = hero?.querySelector('.hero__copy');
      const dock = this.root.querySelector('.occasion-dock');
      if (!hero || !copy || !dock) return;
      const desktop = matchMedia('(min-width: 821px)');
      let frame = 0;
      const update = () => {
        if (this.abort.signal.aborted) return;
        if (!desktop.matches) {
          dock.style.removeProperty('--occasion-hero-overlap');
          return;
        }
        const gap = parseFloat(getComputedStyle(dock).getPropertyValue('--occasion-hero-gap')) || 48;
        const overlap = Math.max(0, hero.getBoundingClientRect().bottom - copy.getBoundingClientRect().bottom - gap);
        dock.style.setProperty('--occasion-hero-overlap', `${overlap.toFixed(2)}px`);
      };
      const queue = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update); };
      const observer = this.observe(ResizeObserver, queue);
      observer.observe(hero);
      observer.observe(copy);
      this.on(desktop, 'change', queue);
      this.cleanups.push(() => { cancelAnimationFrame(frame); dock.style.removeProperty('--occasion-hero-overlap'); });
      update();
    }

    mountHeroMessages() {
      const component = this.root.querySelector('[data-hero-messages]');
      const toggle = this.root.querySelector('[data-hero-messages-toggle]');
      if (!component || !toggle) return;
      customElements.whenDefined('announcement-bar-component').then(() => {
        if (this.abort.signal.aborted) return;
        const mobile = matchMedia('(max-width: 820px)');
        let visible = false, userPaused = false, focused = false;
        const manual = () => reducedMotion.matches || Boolean(window.Shopify?.designMode);
        const sync = () => {
          component.pause();
          const manualMode = manual();
          component.setAttribute('aria-live', manualMode ? 'polite' : 'off');
          if (visible && mobile.matches && !document.hidden && !focused && !manualMode && !userPaused) {
            component.setAttribute('autoplay', component.dataset.interval);
            component.play();
          } else component.removeAttribute('autoplay');
          toggle.setAttribute('aria-label', manualMode ? toggle.dataset.nextLabel : userPaused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel);
          toggle.querySelector('[data-message-pause]').hidden = manualMode || userPaused;
          toggle.querySelector('[data-message-play]').hidden = manualMode || !userPaused;
          toggle.querySelector('[data-message-next]').hidden = !manualMode;
          component.dataset.messageMode = manualMode ? 'manual' : userPaused ? 'paused' : 'automatic';
        };
        this.observe(IntersectionObserver, ([entry]) => {
          visible = entry.isIntersecting;
          sync();
        }).observe(component);
        this.on(toggle, 'click', () => {
          if (manual()) component.next();
          else {
            userPaused = !userPaused;
            if (!userPaused) focused = false; // An explicit resume also works while the control keeps focus.
          }
          sync();
        });
        this.on(component.closest('.freshness-strip'), 'focusin', () => { focused = true; sync(); });
        this.on(component.closest('.freshness-strip'), 'focusout', (event) => {
          if (event.currentTarget.contains(event.relatedTarget)) return;
          focused = false;
          sync();
        });
        this.on(mobile, 'change', sync);
        this.on(reducedMotion, 'change', sync);
        this.on(document, 'visibilitychange', sync);
        this.on(window, 'pageshow', sync);
        this.cleanups.push(() => { component.pause(); component.removeAttribute('autoplay'); toggle.hidden = true; });
        toggle.hidden = false;
        sync();
      });
    }

    mountRails() {
      const buttons = [...this.root.querySelectorAll('[data-rail-target]')];
      const rails = [...this.root.querySelectorAll('.rail')];
      rails.forEach((rail) => {
        if (rail.matches('.occasion-rail, [data-mouse-drag]')) this.cleanups.push(enableMouseDrag(rail));
        const controls = buttons.filter((button) => button.dataset.railTarget === rail.id);
        const progress = this.root.querySelector(`[data-rail-progress="${CSS.escape(rail.id)}"]`);
        const hint = this.root.querySelector(`[data-rail-hint="${CSS.escape(rail.id)}"]`);
        let frame = 0;
        const rtl = () => getComputedStyle(rail).direction === 'rtl';
        const update = () => {
          const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
          const position = Math.min(max, Math.abs(rail.scrollLeft));
          if (hint) {
            hint.hidden = max <= 2 || position > 2;
            rail.toggleAttribute('data-rail-overflow', max > 2);
          }
          controls.forEach((button) => {
            button.disabled = button.dataset.direction === 'previous' ? position <= 2 : max - position <= 2;
          });
          if (progress) {
            const ratio = rail.scrollWidth > 0 ? Math.min(1, rail.clientWidth / rail.scrollWidth) : 1;
            const thumb = Math.max(18, ratio * 100);
            const movement = max ? (position / max) * (100 - thumb) / (thumb / 100) : 0;
            progress.style.width = `${thumb}%`;
            progress.style.transform = `translateX(${rtl() ? -movement : movement}%)`;
          }
        };
        const queue = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update); };
        controls.forEach((button) => this.on(button, 'click', () => {
          const item = rail.querySelector(':scope > li');
          const step = item ? item.getBoundingClientRect().width + (parseFloat(getComputedStyle(rail).columnGap) || 0) : rail.clientWidth * .8;
          const direction = (button.dataset.direction === 'previous' ? -1 : 1) * (rtl() ? -1 : 1);
          rail.scrollBy({ left: direction * step, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        }));
        this.on(rail, 'scroll', queue, { passive: true });
        this.observe(ResizeObserver, queue).observe(rail);
        this.cleanups.push(() => cancelAnimationFrame(frame));
        queue();
      });
    }

    mountTabs() {
      this.root.querySelectorAll('[data-product-tabs], [data-budget-tabs]').forEach((widget) => {
        const tabs = [...widget.querySelectorAll('[role="tab"]')];
        const panels = tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
        if (!tabs.length || panels.some((panel) => !panel || !widget.contains(panel))) return;
        const products = widget.hasAttribute('data-product-tabs');
        let frame = 0;
        const activate = (index, focus = false, scroll = false) => {
          tabs.forEach((tab, i) => {
            tab.setAttribute('aria-selected', String(i === index));
            tab.tabIndex = i === index ? 0 : -1;
            panels[i].hidden = i !== index;
          });
          widget.dataset.activeIndex = String(index);
          if (focus) tabs[index].focus({ preventScroll: true });
          if (scroll && products) {
            const tablist = tabs[index].closest('[role="tablist"]');
            const item = tabs[index].getBoundingClientRect();
            const container = tablist.getBoundingClientRect();
            const rtl = getComputedStyle(tablist).direction === 'rtl';
            const offset = rtl ? item.right - container.right + 2 : item.left - container.left - 2;
            tablist.scrollBy({ left: offset, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
          }
          const panel = panels[index];
          panel.querySelectorAll('.rail').forEach((rail) => { rail.scrollLeft = 0; });
          panel.querySelectorAll('img').forEach((image) => {
            if (image.loading === 'lazy') image.loading = 'eager';
            if (image.sizes?.startsWith('auto,')) image.sizes = image.sizes.replace(/^auto,\s*/, '');
          });
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => {
            panel.querySelectorAll('.rail').forEach((rail) => rail.dispatchEvent(new Event('scroll')));
          });
        };
        tabs.forEach((tab, index) => {
          this.on(tab, 'click', () => activate(index, false, true));
          this.on(tab, 'keydown', (event) => {
            const rtl = getComputedStyle(widget).direction === 'rtl';
            let next = index;
            if (event.key === 'ArrowRight') next += rtl ? -1 : 1;
            else if (event.key === 'ArrowLeft') next += rtl ? 1 : -1;
            else if (event.key === 'Home') next = 0;
            else if (event.key === 'End') next = tabs.length - 1;
            else return;
            event.preventDefault();
            next = Math.max(0, Math.min(tabs.length - 1, next));
            activate(next, true, true);
          });
        });
        this.on(document, 'shopify:block:select', (event) => {
          const index = panels.findIndex((panel) => panel === event.target || panel.contains(event.target));
          if (index !== -1) activate(index, false, true);
        });
        this.cleanups.push(() => cancelAnimationFrame(frame));
        activate(Math.max(0, tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')));
      });
    }

    mountBudgetEntrance() {
      const widget = this.root.querySelector('[data-budget-tabs]');
      const stage = widget?.querySelector('.budget-previews');
      const photo = stage?.querySelector('.budget-preview:not([hidden]) .budget-photo');
      if (!photo || widget.dataset.budgetEntered || reducedMotion.matches ||
          window.Shopify?.designMode || !('IntersectionObserver' in window)) return;

      let observer, timeout = 0, started = false, settled = false;
      const settle = () => {
        settled = true;
        widget.dataset.budgetEntered = 'true';
        observer?.disconnect();
        clearTimeout(timeout);
        photo.classList.remove('is-budget-photo-pending', 'is-budget-photo-entering');
      };
      const enter = () => {
        if (started || settled) return;
        started = true;
        widget.dataset.budgetEntered = 'true';
        observer.disconnect();
        if (reducedMotion.matches || photo.closest('[hidden]')) { settle(); return; }
        photo.classList.replace('is-budget-photo-pending', 'is-budget-photo-entering');
        // Clear compositor hints even if animationend is interrupted by browser UI.
        timeout = setTimeout(settle, 1000);
      };
      observer = this.observe(IntersectionObserver, ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= .4) enter();
      }, { threshold: .4 });
      photo.classList.add('is-budget-photo-pending');
      observer.observe(stage); // Observe the stable panel, not the moving photograph.
      this.on(photo, 'animationend', (event) => {
        if (event.animationName === 'rdb-budget-photo-arrive') settle();
      });
      this.on(widget, 'focusin', settle);
      this.on(widget, 'click', (event) => {
        if (event.target.closest('[role="tab"]')) settle();
      });
      this.on(reducedMotion, 'change', () => { if (reducedMotion.matches) settle(); });
      this.on(window, 'pageshow', (event) => { if (event.persisted) settle(); });
      this.cleanups.push(settle);
    }

    mountReels() {
      this.root.querySelectorAll('.social-video').forEach((container) => {
        const video = container.querySelector('video[data-reel-video]');
        const button = container.querySelector('[data-video-toggle]');
        if (!video || !button) return;
        const fallback = container.querySelector('[data-video-fallback]');
        let visible = false, paused = false, started = false, failed = false, pending = false;
        video.muted = true;
        const update = () => {
          const playing = !video.paused && !video.ended && !failed;
          button.setAttribute('aria-pressed', String(playing));
          button.setAttribute('aria-label', playing ? button.dataset.pauseLabel : button.dataset.playLabel);
          button.querySelector('[data-play-icon]').hidden = playing;
          button.querySelector('[data-pause-icon]').hidden = !playing;
          container.dataset.playing = String(playing);
        };
        const start = () => {
          if (failed || pending || document.hidden || !video.paused || this.abort.signal.aborted) return;
          if (!video.getAttribute('src') && video.dataset.src) { video.src = video.dataset.src; video.load(); }
          pending = true;
          video.play().catch(() => {}).finally(() => { pending = false; update(); });
        };
        const sync = () => {
          if (document.hidden || !visible || paused || failed || this.abort.signal.aborted) video.pause();
          else if (started || (!reducedMotion.matches && !navigator.connection?.saveData)) start();
          else video.pause();
        };
        this.on(video, 'play', () => { if (document.hidden || !visible || paused || this.abort.signal.aborted) video.pause(); update(); });
        this.on(video, 'pause', update);
        this.on(video, 'ended', update);
        this.on(video, 'error', () => { failed = true; video.pause(); button.hidden = true; if (fallback) fallback.hidden = false; update(); });
        this.on(button, 'click', () => {
          if (!video.paused || pending) { paused = true; video.pause(); }
          else { paused = false; started = true; visible = true; start(); }
          update();
        });
        this.observe(IntersectionObserver, ([entry]) => {
          visible = entry.isIntersecting && entry.intersectionRatio >= .3;
          sync();
        }, { threshold: [0, .3] }).observe(container);
        this.on(document, 'visibilitychange', sync);
        this.on(reducedMotion, 'change', sync);
        this.on(navigator.connection, 'change', sync);
        this.cleanups.push(() => { video.pause(); video.removeAttribute('src'); video.load(); });
        update();
      });
    }

    mountClientStrip() {
      const strip = this.root.querySelector('[data-client-strip]');
      if (!strip) return;
      const viewport = strip.querySelector('[data-client-viewport]');
      const group = viewport?.querySelector('.company-logos');
      const toggle = strip.querySelector('[data-client-toggle]');
      if (!viewport || !group || !toggle) return;
      let stride = 0, position = 0, frame = 0, time = 0, timer = 0;
      let visible = false, hovered = false, focused = false, interacting = false, paused = false, started = false;
      const clones = [];
      const enabled = () => !paused && (!reducedMotion.matches || started);
      const canMove = () => enabled() && visible && !document.hidden && !hovered && !focused && !interacting && stride > 0;
      const stop = () => { cancelAnimationFrame(frame); frame = 0; time = 0; };
      const tick = (now) => {
        if (!canMove()) { stop(); return; }
        const elapsed = time ? Math.min(now - time, 50) : 0;
        time = now;
        position = (position + elapsed * .022) % stride;
        viewport.scrollLeft = position * (getComputedStyle(viewport).direction === 'rtl' ? -1 : 1);
        frame = requestAnimationFrame(tick);
      };
      const sync = () => {
        const playing = enabled();
        toggle.setAttribute('aria-label', playing ? toggle.dataset.pauseLabel : toggle.dataset.playLabel);
        toggle.setAttribute('aria-pressed', String(playing));
        strip.dataset.paused = String(!playing);
        toggle.querySelector('[data-client-pause-icon]').hidden = !playing;
        toggle.querySelector('[data-client-play-icon]').hidden = playing;
        if (!canMove()) stop();
        else if (!frame) { position = Math.abs(viewport.scrollLeft) % stride; frame = requestAnimationFrame(tick); }
      };
      const rebuild = () => {
        stop();
        clones.splice(0).forEach((clone) => clone.remove());
        stride = group.getBoundingClientRect().width + (parseFloat(getComputedStyle(viewport).columnGap) || 0);
        if (stride > 0) {
          for (let i = 0; i < Math.ceil(viewport.clientWidth / stride) + 1; i++) {
            const clone = group.cloneNode(true);
            clone.setAttribute('aria-hidden', 'true');
            clone.inert = true;
            clone.querySelectorAll('[id], [data-shopify-editor-block]').forEach((node) => { node.removeAttribute('id'); node.removeAttribute('data-shopify-editor-block'); });
            viewport.append(clone);
            clones.push(clone);
          }
          viewport.scrollLeft %= stride;
        }
        sync();
      };
      const settle = () => {
        if (!interacting) return;
        clearTimeout(timer);
        timer = setTimeout(() => { interacting = false; sync(); }, 1600);
      };
      this.on(viewport, 'pointerenter', (event) => { if (event.pointerType === 'mouse') { hovered = true; sync(); } });
      this.on(viewport, 'pointerleave', () => { hovered = false; sync(); });
      this.on(viewport, 'pointerdown', () => { interacting = true; clearTimeout(timer); sync(); });
      this.on(window, 'pointerup', settle, { passive: true });
      this.on(viewport, 'pointercancel', settle, { passive: true });
      this.on(viewport, 'wheel', () => { interacting = true; sync(); settle(); }, { passive: true });
      this.on(viewport, 'focusin', () => { focused = true; sync(); });
      this.on(viewport, 'focusout', (event) => { focused = viewport.contains(event.relatedTarget); sync(); });
      this.on(toggle, 'click', () => { const wasEnabled = enabled(); paused = wasEnabled; started = !wasEnabled; sync(); });
      this.on(document, 'visibilitychange', sync);
      this.on(reducedMotion, 'change', () => { started = false; sync(); });
      this.observe(IntersectionObserver, ([entry]) => { visible = entry.isIntersecting; sync(); }).observe(strip);
      const observer = this.observe(ResizeObserver, rebuild);
      observer.observe(viewport);
      observer.observe(group);
      this.cleanups.push(() => { stop(); clearTimeout(timer); clones.forEach((clone) => clone.remove()); });
      rebuild();
      toggle.hidden = false;
    }

    destroy() {
      this.abort.abort();
      this.cleanups.forEach((cleanup) => cleanup());
      instances.delete(this.root);
    }
  }

  const roots = (scope) => scope.matches?.(selector) ? [scope] : [...scope.querySelectorAll(selector)];
  const initialize = (scope = document) => roots(scope).forEach((root) => {
    if (!instances.has(root)) instances.set(root, new HomeSection(root));
  });
  window.rosariumDubaiHome = { initialize };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => roots(event.target).forEach((root) => instances.get(root)?.destroy()));
})();
